import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Shield, Zap, CheckCircle, ArrowRight, Wrench } from 'lucide-react';
import { PHONES, CONTACT, SERVICES } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import { SEOHead, BreadcrumbSchema } from '../components/SEOHead';
import Breadcrumb from '../components/Breadcrumb';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';
import { Helmet } from 'react-helmet-async';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

const DETAILED_SERVICES = [
  {
    id: 'acil-lastik-tamiri',
    seoSlug: 'lastik-tamiri',
    icon: Zap,
    title: 'Acil Lastik Tamiri',
    keywords: 'acil lastik tamiri, lastik tamir, lastik yama',
    description: 'Lastiğiniz patladı, delik mi açıldı? Mobil ekibimiz bulunduğunuz konuma gelip yerinde profesyonel lastik tamiri yapar. Lastik yaması, tüp tamiri ve acil müdahale hizmetimiz 7/24 aktiftir.',
    details: ['Yerinde lastik yaması ve tamiri', 'Çivi ve vida kaynaklı delik onarımı', 'Yan kesik değerlendirmesi', 'Tüm lastik markaları için geçerli'],
    faqs: [
      { q: 'Lastik tamiri ne kadar sürer?', a: 'Basit bir lastik tamiri ortalama 15-20 dakikada tamamlanır.' },
      { q: 'Tamir edilen lastik güvenli mi?', a: 'Evet, profesyonel yama ve tamir malzemeleri kullanırız. 24 ay garanti veririz.' },
    ],
  },
  {
    id: 'lastik-degisimi',
    seoSlug: 'lastik-degisimi',
    icon: Wrench,
    title: 'Lastik Değişimi',
    keywords: 'lastik değişimi, lastik montajı, yeni lastik takma',
    description: 'Yeni lastik montajı için servise gitmenize gerek yok. Mobil aracımızla yerinize gelip lastiğinizi değiştiriyoruz. Goodyear, Michelin, Bridgestone, Lassa ve tüm markalar stokta.',
    details: ['Yerinde lastik montajı ve sökümü', 'Tüm büyük markalar stokta', 'Binek ve ticari araç lastikleri', 'Balans ayarı dahil'],
    faqs: [
      { q: 'Lastik değişimi ücreti ne kadar?', a: 'Lastik markası ve ebadına göre değişir. Aramanız halinde net fiyat bilgisi veririz.' },
      { q: 'Hangi lastik markaları mevcut?', a: 'Goodyear, Michelin, Bridgestone, Lassa, Pirelli, Continental, Petlas ve daha fazlası.' },
    ],
  },
  {
    id: 'kis-lastigi',
    seoSlug: 'kis-lastigi-takma',
    icon: Shield,
    title: 'Kış Lastiği Takma',
    keywords: 'kış lastiği takma, kış lastiği değişimi, mevsimlik lastik',
    description: 'Kış lastiği takma ve çıkarma hizmetimiz ile mevsim değişikliklerinde servise gitmenize gerek kalmaz. Ekibimiz yerinize gelir, kış lastiklerinizi güvenle takar.',
    details: ['Kış lastiği takma ve çıkarma', 'Mevsimlik lastik saklama bilgisi', 'Kartepe dağ yolları için zincir takma', 'Lastik basınç kontrolü dahil'],
    faqs: [
      { q: 'Kış lastiği ne zaman takılmalı?', a: 'Kasım ayı başından itibaren kış lastiği takmanızı öneriyoruz. Yasal zorunluluk 1 Aralık\'tan itibaren başlar.' },
      { q: 'Kış lastiği takma ücreti ne kadar?', a: 'Araç tipine göre değişir. Arayın, net fiyat verelim.' },
    ],
  },
  {
    id: 'sibop-tamiri',
    seoSlug: 'sibop-tamiri',
    icon: CheckCircle,
    title: 'Sibop Tamiri ve Değişimi',
    keywords: 'sibop tamiri, sibop değişimi, lastik sibop',
    description: 'Sibop arızası lastiğin sürekli hava kaybetmesine neden olur. Sibop tamiri ve değişimi hizmetimiz ile sorununuzu yerinde çözüyoruz.',
    details: ['Sibop tamiri ve değişimi', 'Hava kaçağı tespiti', 'Lastik basınç ayarı', 'Tüm sibop tipleri için'],
    faqs: [
      { q: 'Sibop arızası tehlikeli mi?', a: 'Evet, sibop arızası lastiğin yavaş yavaş inmesine neden olur ve sürüş güvenliğini azaltır.' },
    ],
  },
  {
    id: 'stepne-hizmeti',
    seoSlug: 'stepne-hizmeti',
    icon: ArrowRight,
    title: 'Stepne Hizmeti',
    keywords: 'stepne montajı, stepne takma, yedek lastik',
    description: 'Stepneniz yoksa veya stepne takamıyorsanız merak etmeyin. Ekibimiz yerinde müdahale eder. Stepne montajı ya da doğrudan yeni lastik takma hizmeti sunarız.',
    details: ['Stepne montajı (yerinde)', 'Stepne yoksa yeni lastik temin', 'Güvenli kaldırma ve montaj', 'Bijon sıkma torku ayarı'],
    faqs: [
      { q: 'Stepnem yoksa ne olur?', a: 'Endişelenmeyin. Stokumuzdan size uygun lastik getirip yerinde takarız.' },
    ],
  },
  {
    id: 'jant-onarimi',
    icon: Clock,
    title: 'Jant Onarımı',
    keywords: 'jant onarımı, jant tamiri, jant düzeltme',
    description: 'Çukur ve kasis kaynaklı jant eğrilmeleri, lastik hava kaçağına neden olabilir. Jant onarım hizmetimiz ile sorununuzu tespit edip çözüyoruz.',
    details: ['Jant eğrilik tespiti', 'Yerinde jant değerlendirmesi', 'Gerekirse jant değişimi', 'Tüm jant tipleri için'],
    faqs: [
      { q: 'Jant eğriliği tehlikeli mi?', a: 'Evet, jant eğriliği lastik hava kaçağı ve dengesiz sürüşe neden olur. Hemen müdahale edilmelidir.' },
    ],
  },
  {
    id: 'zincir-takma',
    seoSlug: 'zincir-takma',
    icon: Shield,
    title: 'Zincir Takma Hizmeti',
    keywords: 'zincir takma, kar zinciri, kış zinciri takma',
    description: 'Kartepe dağ yolları, Bolu geçişi veya buzlu yollarda kar zinciri takma hizmetimiz ile güvenle yolunuza devam edin.',
    details: ['Kar zinciri takma ve çıkarma', 'Kartepe ve dağ yollarında hizmet', 'Doğru zincir boyutu kontrolü', 'Güvenli sürüş tavsiyeleri'],
    faqs: [
      { q: 'Zincir takma ne kadar sürer?', a: 'Ortalama 15-20 dakikada tüm tekerleklere zincir takılır.' },
    ],
  },
];

export default function Hizmetlerimiz() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": DETAILED_SERVICES.map((s, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Service",
        "name": s.title,
        "description": s.description,
        "provider": {
          "@type": "LocalBusiness",
          "name": "7/24 Lastik Yol Yardım",
          "telephone": "+905349687611",
        },
        "areaServed": { "@type": "State", "name": "Kocaeli" },
      },
    })),
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": DETAILED_SERVICES.flatMap(s => s.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    }))),
  };

  return (
    <main data-testid="hizmetlerimiz-page" style={{ background: 'var(--bg)' }}>
      <SEOHead
        path="/hizmetlerimiz"
        title="Hizmetlerimiz | Kocaeli Lastik Yol Yardım 7/24 Mobil Lastikçi"
        description="Kocaeli lastik yol yardım hizmetleri: Acil lastik tamiri, lastik değişimi, kış lastiği takma, sibop tamiri, stepne hizmeti, jant onarımı ve zincir takma. 7/24 mobil lastikçi."
      />
      <BreadcrumbSchema items={[
        { name: 'Anasayfa', url: '/' },
        { name: 'Hizmetlerimiz' },
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section style={{ background: 'var(--bg)', paddingTop: 86, borderBottom: '1px solid var(--border)' }}>
        <div className="wrap" style={{ padding: '3.5rem 1.5rem 3rem' }}>
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: 'Hizmetlerimiz' },
          ]} />

          <LiveBadge text="Tüm Hizmetler Aktif" />

          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(2rem, 5.5vw, 3.5rem)', color: 'var(--text)',
            lineHeight: 1.05, letterSpacing: '-.04em', margin: '1rem 0 .5rem',
          }}>
            Hizmetlerimiz
          </h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.4rem, 4vw, 2.5rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1rem',
          }}>
            7/24 Mobil Lastik Servisi
          </p>
          <p style={{ fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)', lineHeight: 1.7, maxWidth: 560 }}>
            Kocaeli genelinde acil lastik tamirinden kış lastiği takımına, sibop değişiminden jant onarımına kadar tüm lastik hizmetlerini <strong style={{ color: 'var(--text)' }}>yerinde</strong> sunuyoruz.
          </p>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', maxWidth: 820 }}>
            {DETAILED_SERVICES.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  id={service.id}
                  data-testid={`service-${service.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: .4, delay: i * .06 }}
                  style={{
                    background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 'var(--rl)', padding: 'clamp(1.5rem, 3vw, 2rem)',
                    transition: 'border-color .2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue-border)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'rgba(229,51,51,.1)', border: '1px solid rgba(229,51,51,.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--red)', flexShrink: 0,
                    }}>
                      <Icon size={18} />
                    </div>
                    <h2 style={{
                      fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                      fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: 'var(--text)', margin: 0,
                    }}>
                      {service.title}
                    </h2>
                  </div>

                  <p style={{ fontSize: '.9rem', color: 'var(--text2)', lineHeight: 1.75, marginBottom: '1.25rem' }}>
                    {service.description}
                  </p>

                  <ul style={{ padding: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '.4rem .75rem', marginBottom: '1.25rem' }} className="service-details-grid">
                    {service.details.map(d => (
                      <li key={d} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.825rem', color: 'var(--text2)' }}>
                        <CheckCircle size={13} style={{ color: 'var(--green)', flexShrink: 0 }} /> {d}
                      </li>
                    ))}
                  </ul>

                  {service.faqs.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                      {service.faqs.map((faq, fi) => (
                        <div key={faq.q} style={{ marginBottom: fi < service.faqs.length - 1 ? '.75rem' : 0 }}>
                          <h3 style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text)', marginBottom: '.2rem', fontFamily: "'Space Grotesk',sans-serif" }}>
                            {faq.q}
                          </h3>
                          <p style={{ fontSize: '.8rem', color: 'var(--text2)', lineHeight: 1.65, margin: 0 }}>
                            {faq.a}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Link to dedicated SEO page */}
                  {service.seoSlug && (
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                      <Link to={`/${service.seoSlug}`} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '.35rem',
                        fontSize: '.82rem', color: 'var(--blue)', fontWeight: 600,
                        textDecoration: 'none', transition: 'opacity .2s',
                      }}>
                        Detaylı Bilgi <ArrowRight size={13} />
                      </Link>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DISTRICT CTA */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap" style={{ textAlign: 'center' }}>
          <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
            Hangi <span style={{ color: 'var(--green)' }}>İlçedesiniz?</span>
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '.5rem', marginBottom: '2rem' }}>
            {DISTRICT_PAGES.map(d => (
              <Link key={d.slug} to={`/${d.slug}`} style={{
                display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 100, padding: '.35rem .9rem', fontSize: '.8rem', color: 'var(--text2)',
                transition: 'border-color .2s, color .2s', textDecoration: 'none',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
              >{d.displayName}</Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA headline="Hemen Arayın." sub="7/24 Yanınızdayız." />

      <style>{`
        @media(max-width:640px) {
          .service-details-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </main>
  );
}
